import React, {useState, useEffect} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {useCart, lineCharge} from '../context/CartContext';
import CheckoutStepper, {type CheckoutStep} from '../components/CheckoutStepper';
import OrderSummary from '../components/OrderSummary';
import AddressForm, {type AddressData} from '../components/AddressForm';
import DeliveryOptions, {type DeliveryMethod} from '../components/DeliveryOptions';
import PaymentWidget from '../components/PaymentWidget';
import {CheckoutService} from '../services/checkoutService';
import BioMatchEnrollmentService from '@/modules/biomatch/services/bioMatchEnrollmentService';
import {toast} from 'react-toastify';
import {ShoppingCart, CheckCircle, Sparkles, Star} from 'lucide-react';
import {getCartRecurringCadence, type CartLine} from '../types';
import Button from '@/shared/components/Button';
import membershipService from '@/modules/patient-dashboard/services/membershipService';
import {USER_DATA_KEY} from '@/modules/patient-dashboard/hooks/useTestsQuery';
import {LATEST_ORDER_QUERY_KEY} from '@/modules/patient-dashboard/hooks/useLatestOrder';
import {BIOMATCH_DASHBOARD_QUERY_KEY} from '@/modules/biomatch/hooks/useBioMatchMarkers';
import {queryClient} from '@/shared/contexts/QueryProvider';
import {randomUUID} from '@/shared/utils/uuid';

const US_STATES = [
    'AL',
    'AK',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DE',
    'FL',
    'GA',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VT',
    'VA',
    'WA',
    'WV',
    'WI',
    'WY',
    'DC',
];

const CheckoutPage: React.FC = () => {
    const {user} = useAuth();
    const {items, subtotalCents, hasRecurring, clearCart, isMember, usesMemberPricing} = useCart();
    const recurringCadence = getCartRecurringCadence(items);
    const navigate = useNavigate();

    const [step, setStep] = useState<CheckoutStep>('details');
    const [submitting, setSubmitting] = useState(false);
    const [checkoutAttemptId, setCheckoutAttemptId] = useState(() => randomUUID());
    const [orderResult, setOrderResult] = useState<{orderId: string; orderNumber: string} | null>(null);

    // Step 1: Details
    const [firstName, setFirstName] = useState(user?.userInfo?.firstName ?? '');
    const [lastName, setLastName] = useState(user?.userInfo?.lastName ?? '');
    const [email, setEmail] = useState(user?.userInfo?.email ?? '');
    const [phone, setPhone] = useState('');
    const [age, setAge] = useState('');
    const [eligibilityState, setEligibilityState] = useState('');

    // Step 2: Delivery — prefilled from the profile; anything the buyer
    // types here wins and is written back to the profile by the BE.
    const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>('standard');
    const [shippingAddress, setShippingAddress] = useState<AddressData>(() => ({
        line1: user?.userInfo?.address1 ?? '',
        line2: user?.userInfo?.address2 ?? '',
        city: user?.userInfo?.city ?? '',
        state: user?.userInfo?.state ?? '',
        zip: user?.userInfo?.zip ?? '',
    }));
    const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
    const [billingAddress, setBillingAddress] = useState<AddressData>({
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
    });

    // Promo / gift card
    const [promoCode, setPromoCode] = useState<string | null>(null);
    const [discountCents, setDiscountCents] = useState(0);
    const [giftCardCode, setGiftCardCode] = useState<string | null>(null);
    const [giftCardBalance, setGiftCardBalance] = useState(0);

    // Employer subsidy quote
    const [employerCoversCents, setEmployerCoversCents] = useState(0);

    const productItems = items
        .filter((i): i is typeof i & {line: {kind: 'product'}} => i.line.kind === 'product')
        .map((i) => ({
            productId: (i.line as Extract<CartLine, {kind: 'product'}>).product.id,
            quantity: i.quantity,
        }));
    const membershipLine = items.find(
        (item): item is typeof item & {line: Extract<CartLine, {kind: 'membership'}>} => item.line.kind === 'membership'
    )?.line;
    const productSubtotalCents = items.reduce(
        (sum, {line, quantity}) =>
            line.kind === 'product' ? sum + lineCharge(line, usesMemberPricing) * quantity : sum,
        0
    );

    useEffect(() => {
        if (!user?.isLoggedIn || productItems.length === 0) {
            setEmployerCoversCents(0);
            return;
        }
        CheckoutService.quote(productItems, membershipLine?.plan, promoCode)
            .then((result) => {
                setEmployerCoversCents(result.employerTotalCents);
            })
            .catch(() => {
                setEmployerCoversCents(0);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, user?.isLoggedIn, promoCode]);

    // Computed totals
    const deliveryCents = productItems.length > 0 && deliveryMethod === 'express' ? 3000 : 0;
    const taxCents = 0;
    const productPayableBeforeGift = Math.max(0, productSubtotalCents - discountCents + deliveryCents);
    const appliedEmployerCoversCents = Math.min(employerCoversCents, productPayableBeforeGift);
    const giftCardApplied = Math.min(
        giftCardBalance,
        Math.max(0, productPayableBeforeGift - appliedEmployerCoversCents)
    );
    const totalCents = Math.max(0, subtotalCents - discountCents + deliveryCents + taxCents - giftCardApplied);
    const patientPayCents = Math.max(0, totalCents - appliedEmployerCoversCents);

    // Handlers
    const handleDetailsSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setStep('delivery');
    };

    // Everything except a pure-membership cart ships something.
    const cartNeedsShipping = items.some((i) => i.line.kind !== 'membership');

    const handleDeliverySubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // The AddressForm inputs are `required`, so the browser normally
        // blocks an incomplete submit — this guard is the backstop.
        if (
            cartNeedsShipping &&
            (!shippingAddress.line1.trim() ||
                !shippingAddress.city.trim() ||
                !shippingAddress.state.trim() ||
                !shippingAddress.zip.trim())
        ) {
            toast.error('Please complete your shipping address (street, city, state, ZIP).');
            return;
        }
        setStep('payment');
    };

    /** Canonical ship-to payload sent to every kit-bearing endpoint.
     *  line2 is omitted (not null) when blank — the combined checkout's
     *  zod schema treats null and undefined differently. */
    const shippingAddressPayload = () => ({
        line1: shippingAddress.line1.trim(),
        line2: shippingAddress.line2.trim() || undefined,
        city: shippingAddress.city.trim(),
        state: shippingAddress.state.trim(),
        zip: shippingAddress.zip.trim(),
    });

    /**
     * Dispatch the cart to the right BE endpoint based on line kinds.
     * Single-kind carts keep their proven dedicated flows; any mixed
     * cart goes through the combined checkout — one charge, one atomic
     * order fan-out on the BE.
     */
    const handleTicketReady = async (ticketId: string) => {
        setSubmitting(true);
        try {
            const kinds = new Set(items.map((i) => i.line.kind));
            const onlyKind = items[0].line.kind;

            if (kinds.size > 1) {
                const curatedLines = items
                    .filter(
                        (i): i is typeof i & {line: Extract<CartLine, {kind: 'curated_rec'}>} =>
                            i.line.kind === 'curated_rec'
                    )
                    .map((i) => i.line);
                const paidCurated = curatedLines.filter((l) => l.patient_amount_cents > 0);
                const freeCurated = curatedLines.filter((l) => l.patient_amount_cents === 0);
                const kitLine = items.find(
                    (i): i is typeof i & {line: Extract<CartLine, {kind: 'custom_kit'}>} => i.line.kind === 'custom_kit'
                )?.line;

                const result = await CheckoutService.submitCombined({
                    checkoutAttemptId,
                    ticketId,
                    clientExpectedAmountCents: patientPayCents,
                    shippingAddress: cartNeedsShipping ? shippingAddressPayload() : undefined,
                    membership: membershipLine ? {plan: membershipLine.plan} : undefined,
                    storefront:
                        productItems.length > 0
                            ? {
                                  customer: {
                                      email,
                                      firstName,
                                      lastName,
                                      phone: phone || undefined,
                                      age: age ? parseInt(age) : undefined,
                                      state: eligibilityState || undefined,
                                  },
                                  shippingAddress,
                                  billingAddress: billingSameAsShipping ? null : billingAddress,
                                  items: productItems,
                                  deliveryMethod,
                                  promoCode,
                                  giftCardCode,
                              }
                            : undefined,
                    curatedRecIds: paidCurated.length > 0 ? paidCurated.map((l) => l.recommendation_id) : undefined,
                    customKit: kitLine ? {mode: kitLine.mode} : undefined,
                });

                if (membershipLine) {
                    queryClient.setQueriesData({queryKey: [USER_DATA_KEY]}, (current: unknown) =>
                        current && typeof current === 'object' ? {...current, subscription: true} : current
                    );
                    void queryClient.invalidateQueries({queryKey: [USER_DATA_KEY]});
                }

                // Fully employer-covered programs are free — enroll them
                // after the paid charge lands. Not atomic with the charge
                // by design: a failure here costs nothing and the patient
                // can re-enroll from BioMatch with one click.
                for (const line of freeCurated) {
                    try {
                        await BioMatchEnrollmentService.enroll(line.recommendation_id);
                    } catch {
                        toast.warn(`"${line.name}" could not be enrolled — enroll it free from your BioMatch page.`);
                    }
                }

                const primaryOrderId =
                    result.storefrontOrderId ??
                    result.customKit?.storefrontOrderId ??
                    result.curatedEnrollments[0]?.storefrontOrderId ??
                    result.membership?.membershipOrderId ??
                    '';
                setOrderResult({
                    orderId: primaryOrderId,
                    orderNumber: result.orderNumber ?? primaryOrderId,
                });
            } else if (onlyKind === 'product') {
                const result = await CheckoutService.submitProducts({
                    checkoutAttemptId,
                    ticketId,
                    customer: {
                        email,
                        firstName,
                        lastName,
                        phone: phone || undefined,
                        age: age ? parseInt(age) : undefined,
                        state: eligibilityState || undefined,
                    },
                    shippingAddress,
                    billingAddress: billingSameAsShipping ? null : billingAddress,
                    items: productItems,
                    deliveryMethod,
                    promoCode,
                    giftCardCode,
                    clientExpectedAmountCents: patientPayCents,
                });
                setOrderResult(result);
            } else if (onlyKind === 'custom_kit') {
                const kitLine = items[0].line as Extract<CartLine, {kind: 'custom_kit'}>;
                const r = await CheckoutService.submitCustomKit({
                    checkoutAttemptId,
                    ticketId,
                    mode: kitLine.mode,
                    // Member-aware — matches the server's
                    // applyMemberDiscount(base, orgPct) so the 422
                    // parity guard doesn't trip for a real member.
                    clientExpectedAmountCents: lineCharge(kitLine, isMember),
                    shippingAddress: shippingAddressPayload(),
                });
                setOrderResult({
                    orderId: String(r.storefrontOrderId),
                    orderNumber: String(r.storefrontOrderId),
                });
            } else if (onlyKind === 'membership') {
                const membershipLine = items[0].line as Extract<CartLine, {kind: 'membership'}>;
                const result = await membershipService.submitCheckout({
                    checkoutAttemptId,
                    plan: membershipLine.plan,
                    ticketId,
                    clientExpectedAmountCents: membershipLine.price_cents,
                });
                queryClient.setQueriesData({queryKey: [USER_DATA_KEY]}, (current: unknown) =>
                    current && typeof current === 'object' ? {...current, subscription: true} : current
                );
                void queryClient.invalidateQueries({queryKey: [USER_DATA_KEY]});
                setOrderResult({
                    orderId: result.storefrontOrderId,
                    orderNumber: result.storefrontOrderId,
                });
            } else {
                // curated_rec — when the employer covers 100%, skip
                // Fortis entirely and call the no-payment enrollment
                // endpoint. The FortisPaymentWidget hands us an empty
                // ticketId in that case.
                const recLine = items[0].line as Extract<CartLine, {kind: 'curated_rec'}>;
                if (recLine.patient_amount_cents === 0) {
                    const enrollment = await BioMatchEnrollmentService.enroll(recLine.recommendation_id);
                    if ('id' in enrollment) {
                        setOrderResult({
                            orderId: String(enrollment.storefront_order_id ?? enrollment.id),
                            orderNumber: String(enrollment.id),
                        });
                    } else {
                        throw new Error('This program now requires payment. Please refresh and try again.');
                    }
                } else {
                    const r = await CheckoutService.submitCuratedRec({
                        checkoutAttemptId,
                        ticketId,
                        recommendationId: recLine.recommendation_id,
                        shippingAddress: shippingAddressPayload(),
                    });
                    setOrderResult({
                        orderId: String(r.storefrontOrderId),
                        orderNumber: String(r.storefrontOrderId),
                    });
                }
            }

            // The dashboard's latest-purchase card caches for 5 minutes —
            // invalidate so the new order is visible the moment the buyer
            // lands back on the dashboard.
            void queryClient.invalidateQueries({queryKey: LATEST_ORDER_QUERY_KEY});
            // A kit/curated purchase consumes the BioMatch offer — the server
            // now suppresses the hero while the order is in flight. Drop the
            // cached assembled view so the dashboard reflects it immediately
            // instead of after the 60s staleTime.
            void queryClient.invalidateQueries({queryKey: BIOMATCH_DASHBOARD_QUERY_KEY});

            setStep('details');
            clearCart();
        } catch (err: unknown) {
            setCheckoutAttemptId(randomUUID());
            const status = (err as {status?: number} | undefined)?.status;
            const data = (err as {data?: {error?: string}} | undefined)?.data;
            if (status === 422) {
                toast.error(data?.error ?? 'Pricing changed since you opened the page. Refresh and try again.');
            } else if (status === 402) {
                toast.error(data?.error ?? 'Payment was declined. Please try a different card.');
            } else {
                toast.error(err instanceof Error ? err.message : 'Checkout failed. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handlePaymentError = (message: string) => {
        toast.error(message);
        setSubmitting(false);
    };

    // Order summary props shared across steps
    const summaryProps = {
        items,
        subtotalCents,
        deliveryCents,
        discountCents,
        giftCardCents: giftCardApplied,
        taxCents,
        totalCents,
        promoCode,
        giftCardCode,
        hasRecurring,
        employerCoversCents: appliedEmployerCoversCents,
        isMember: usesMemberPricing,
        promoSubtotalCents: productSubtotalCents,
        onApplyPromo: (code: string, discount: number) => {
            setPromoCode(code);
            setDiscountCents(discount);
        },
        onClearPromo: () => {
            setPromoCode(null);
            setDiscountCents(0);
        },
        onApplyGiftCard: (code: string, balance: number) => {
            setGiftCardCode(code);
            setGiftCardBalance(balance);
        },
        onClearGiftCard: () => {
            setGiftCardCode(null);
            setGiftCardBalance(0);
        },
    };

    // Empty cart guard
    if (items.length === 0 && !orderResult) {
        return (
            <div className="text-center py-20">
                <ShoppingCart className="h-16 w-16 text-mld-muted-text/60 mx-auto mb-4" />
                <h2 className="text-2xl font-semibold text-mld-text-primary mb-2">No items to check out</h2>
                <Link
                    to="/shop"
                    className="inline-block mt-4 px-6 py-3 bg-mld-success text-white font-semibold rounded-full hover:bg-mld-success/90 transition-colors">
                    Browse Tests
                </Link>
            </div>
        );
    }

    // Confirmation
    if (orderResult) {
        return (
            <div className="max-w-lg mx-auto text-center py-16">
                <CheckCircle className="h-16 w-16 text-mld-success mx-auto mb-4" />
                <h1 className="text-3xl font-bold text-mld-text-primary mb-2">Order Confirmed</h1>
                <p className="text-mld-muted-text mb-2">
                    Order <span className="font-semibold">{orderResult.orderNumber}</span>
                </p>
                <p className="text-mld-muted-text mb-8">
                    We&apos;ll ship your test kit shortly. You&apos;ll receive a confirmation email at{' '}
                    <span className="font-medium">{email}</span>.
                </p>
                <Button variant="primary" size="lg" onClick={() => navigate('/dashboard')} className="rounded-full">
                    Back to Dashboard
                </Button>
            </div>
        );
    }

    return (
        <div>
            <CheckoutStepper current={step} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left column — form steps */}
                <div className="lg:col-span-2">
                    {/* ── Step 1: Details ──────────────────────────────── */}
                    {step === 'details' && (
                        <form
                            onSubmit={handleDetailsSubmit}
                            className="bg-mld-menu-background rounded-xl border border-mld-panel-border p-6">
                            <h2 className="text-lg font-semibold text-mld-text-primary mb-4">Your details</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Field label="First name" required value={firstName} onChange={setFirstName} />
                                <Field label="Last name" required value={lastName} onChange={setLastName} />
                                <Field
                                    label="Email address"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={setEmail}
                                    className="sm:col-span-2"
                                />
                                <Field
                                    label="Phone number (optional)"
                                    type="tel"
                                    value={phone}
                                    onChange={setPhone}
                                    className="sm:col-span-2"
                                />
                            </div>

                            <h2 className="text-lg font-semibold text-mld-text-primary mt-6 mb-4">
                                Eligibility requirements
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Field
                                    label="Age"
                                    type="number"
                                    required
                                    value={age}
                                    onChange={setAge}
                                    placeholder="e.g. 33"
                                />
                                <label className="block">
                                    <span className="block text-sm text-mld-muted-text mb-1">
                                        State <span className="text-mld-danger">*</span>
                                    </span>
                                    <select
                                        required
                                        value={eligibilityState}
                                        onChange={(e) => setEligibilityState(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-mld-menu-background focus:outline-none focus:ring-2 focus:ring-mld-success/50">
                                        <option value="">Select state</option>
                                        {US_STATES.map((s) => (
                                            <option key={s} value={s}>
                                                {s}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>

                            <p className="text-xs text-mld-muted-text mt-4">
                                All test takers must be at least 18 years of age. Fees include the cost of clinician
                                services from PWNHealth. Test availability varies by state.
                            </p>

                            <Button type="submit" variant="primary" size="lg" fullWidth className="mt-6 rounded-full">
                                Continue
                            </Button>
                        </form>
                    )}

                    {/* ── Step 2: Delivery ─────────────────────────────── */}
                    {step === 'delivery' && (
                        <form onSubmit={handleDeliverySubmit} className="space-y-6">
                            {/* Items being shipped */}
                            <div className="bg-mld-menu-background rounded-xl border border-mld-panel-border p-6">
                                <h2 className="text-lg font-semibold text-mld-text-primary mb-4">
                                    Items being shipped
                                </h2>
                                <div className="space-y-3">
                                    {items.map(({line, quantity}) => (
                                        <div key={line.key} className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded bg-mld-menu-background flex-shrink-0 overflow-hidden flex items-center justify-center">
                                                {line.image_url ? (
                                                    <img
                                                        src={line.image_url}
                                                        alt=""
                                                        className="w-full h-full object-contain p-0.5"
                                                    />
                                                ) : line.kind === 'custom_kit' ? (
                                                    <Sparkles className="h-3 w-3 text-mld-primary" />
                                                ) : line.kind === 'curated_rec' ? (
                                                    <Star className="h-3 w-3 text-mld-secondary" />
                                                ) : (
                                                    <ShoppingCart className="h-3 w-3 text-mld-muted-text/60" />
                                                )}
                                            </div>
                                            <p className="text-sm text-mld-text-primary flex-1 truncate">{line.name}</p>
                                            <p className="text-xs text-mld-muted-text">
                                                {line.kind === 'product'
                                                    ? `Qty: ${quantity}`
                                                    : line.kind === 'custom_kit' && line.mode === 'member'
                                                      ? 'Membership'
                                                      : line.kind === 'custom_kit'
                                                        ? 'One-time kit'
                                                        : line.kind === 'membership'
                                                          ? 'My Labs+ membership'
                                                          : 'Program'}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Delivery options */}
                            <div className="bg-mld-menu-background rounded-xl border border-mld-panel-border p-6">
                                <DeliveryOptions value={deliveryMethod} onChange={setDeliveryMethod} />
                                <p className="text-xs text-mld-muted-text mt-3">
                                    Return shipping to the lab is sent standard USPS ground shipping. Express shipping
                                    is not available for PO boxes and some addresses. Shipping delays or holidays may
                                    impact the estimated service.
                                </p>
                            </div>

                            {/* Shipping address */}
                            <div className="bg-mld-menu-background rounded-xl border border-mld-panel-border p-6">
                                <AddressForm
                                    value={shippingAddress}
                                    onChange={setShippingAddress}
                                    label="Shipping address"
                                />
                            </div>

                            {/* Billing address */}
                            <div className="bg-mld-menu-background rounded-xl border border-mld-panel-border p-6">
                                <h3 className="text-sm font-semibold text-mld-text-primary mb-3">Billing address</h3>
                                <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-300 cursor-pointer mb-2">
                                    <input
                                        type="radio"
                                        name="billing"
                                        checked={billingSameAsShipping}
                                        onChange={() => setBillingSameAsShipping(true)}
                                        className="accent-mld-success"
                                    />
                                    <span className="text-sm text-mld-text-primary">Same as shipping</span>
                                </label>
                                <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-300 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="billing"
                                        checked={!billingSameAsShipping}
                                        onChange={() => setBillingSameAsShipping(false)}
                                        className="accent-mld-success"
                                    />
                                    <span className="text-sm text-mld-text-primary">
                                        Use a different billing address
                                    </span>
                                </label>
                                {!billingSameAsShipping && (
                                    <div className="mt-4">
                                        <AddressForm value={billingAddress} onChange={setBillingAddress} />
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-3">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="lg"
                                    onClick={() => setStep('details')}
                                    className="rounded-full">
                                    Back
                                </Button>
                                <Button type="submit" variant="primary" size="lg" className="flex-1 rounded-full">
                                    Continue to Payment
                                </Button>
                            </div>
                        </form>
                    )}

                    {/* ── Step 3: Payment ──────────────────────────────── */}
                    {step === 'payment' && (
                        <div className="bg-mld-menu-background rounded-xl border border-mld-panel-border p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-mld-text-primary">Payment</h2>
                                <Button type="button" variant="ghost" size="sm" onClick={() => setStep('delivery')}>
                                    Back
                                </Button>
                            </div>

                            {/* Customer + address summary */}
                            <div className="bg-mld-menu-background rounded-lg p-4 mb-6 text-sm text-mld-muted-text">
                                <p className="font-medium text-mld-text-primary">
                                    {firstName} {lastName}
                                </p>
                                <p>{email}</p>
                                <p>
                                    {shippingAddress.line1}
                                    {shippingAddress.line2 ? `, ${shippingAddress.line2}` : ''}
                                </p>
                                <p>
                                    {shippingAddress.city}, {shippingAddress.state} {shippingAddress.zip}
                                </p>
                                <p className="mt-1">
                                    Delivery: {deliveryMethod === 'express' ? 'Express ($30.00)' : 'Standard (Free)'}
                                </p>
                            </div>

                            {hasRecurring && recurringCadence && (
                                <div className="mb-4 rounded-md border border-mld-primary/30 bg-mld-primary/5 p-3 text-xs text-mld-text-primary">
                                    <span className="font-medium">Recurring billing.</span> Your card will be charged
                                    today for the full total. Subscription
                                    {recurringCadence === 'mixed'
                                        ? ' items renew according to their listed billing schedules'
                                        : ` items renew on the same date each ${
                                              recurringCadence === 'annual' ? 'year' : 'month'
                                          }`}{' '}
                                    and can be cancelled anytime from your dashboard.
                                </div>
                            )}

                            {totalCents === 0 ? (
                                <div>
                                    <p className="text-sm text-mld-success font-medium mb-4">
                                        Gift card covers the full order. No payment needed.
                                    </p>
                                    <Button
                                        type="button"
                                        variant="primary"
                                        size="lg"
                                        fullWidth
                                        onClick={() => handleTicketReady('')}
                                        loading={submitting}
                                        className="rounded-full">
                                        {submitting ? 'Placing Order...' : 'Place Order'}
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    {submitting ? (
                                        <div className="flex items-center justify-center py-8">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mld-success" />
                                            <span className="ml-3 text-sm text-mld-muted-text">
                                                Processing your order...
                                            </span>
                                        </div>
                                    ) : (
                                        <PaymentWidget
                                            onTokenReady={handleTicketReady}
                                            onError={handlePaymentError}
                                            createIntention={
                                                membershipLine
                                                    ? () => membershipService.createCheckoutTicket()
                                                    : undefined
                                            }
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Right column — Order summary */}
                <div className="lg:col-span-1">
                    <OrderSummary {...summaryProps} showPromoFields={step === 'delivery' || step === 'payment'} />
                </div>
            </div>
        </div>
    );
};

function Field({
    label,
    value,
    onChange,
    type = 'text',
    required,
    className,
    placeholder,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    type?: string;
    required?: boolean;
    className?: string;
    placeholder?: string;
}) {
    return (
        <label className={`block ${className ?? ''}`}>
            <span className="block text-sm text-mld-muted-text mb-1">
                {label}
                {required && <span className="text-mld-danger ml-0.5">*</span>}
            </span>
            <input
                type={type}
                required={required}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-mld-success/50"
            />
        </label>
    );
}

export default CheckoutPage;
